import { ColumnInformationDto } from "components/annotations/interface";
import { AitRowGroupData } from "./AitRowGroupData";
import { AitHeaderGroupData } from "./interface";
import { newHeaderCell } from "./newCell";
import { trDuplicate } from "./trDuplicate";
/**
 * Duplicate a column and insert the duplicate immediately to the right of the original.
 *
 * The function duplicates both header and body cells for the specified
 * `columnIndex`, assigns a fresh `columnId` to the duplicated column, and
 * updates `columnInformation` to include the new entry. When `newLength` is
 * less than or equal to the current number of columns the function returns the
 * original data unchanged.
 *
 * @template T - The cell payload type
 * @param headerData - Header group data, or `false` if no header is present
 * @param bodyData - Body row groups
 * @param columnIndex - Zero-based index of the column to duplicate
 * @param defaultCellWidth - Default width to apply to newly-created header cells
 * @param blankT - Default blank payload value for header cell construction
 * @param columnInformation - Array of ColumnInformationDto describing existing columns
 * @param newLength - Desired number of total columns after duplication
 * @returns Object containing updated `headerData`, `bodyData`, and `columnInformation`
 * @throws Error for invalid inputs (for example: missing original column info or empty body)
 */
export const trDuplicateColumn = <T>(
  headerData: AitHeaderGroupData<T> | false,
  bodyData: AitRowGroupData<T>[],
  columnIndex: number,
  defaultCellWidth: number,
  blankT: T,
  columnInformation: ColumnInformationDto[],
  newLength: number,
): {
  headerData: AitHeaderGroupData<T> | false;
  bodyData: AitRowGroupData<T>[];
  columnInformation: ColumnInformationDto[];
} => {
  // Check column index is acceptable
  if (columnIndex < 0) throw new Error("Column index is too low");
  if (bodyData.length === 0) throw new Error("No row groups in body data");
  if (bodyData.some((rg) => rg.rows.length === 0)) throw new Error("No rows in body data");
  if (columnIndex >= bodyData[0].rows[0].cells.length) throw new Error("Column index is too high");

  // Add new columnInformation entry for the duplicated column
  const originalColumnInfo = columnInformation.find((ci) => ci.originalColumnIndex === columnIndex);
  if (!originalColumnInfo) throw new Error("Original column information not found");

  if (newLength <= bodyData[0].rows[0].cells.length)
    return {
      headerData,
      bodyData,
      columnInformation,
    };

  // Generate a new unique columnId for the duplicated column
  const newColumnId = crypto.randomUUID();

  // Copy the annotation from the original column with the new columnId
  const newColumnInfo: ColumnInformationDto = {
    ...originalColumnInfo,
    columnId: newColumnId,
    originalColumnIndex: columnIndex + 1,
  };
  const updatedColumnInformation = [
    ...columnInformation.slice(0, columnIndex + 1),
    newColumnInfo,
    ...columnInformation.slice(columnIndex + 1).map((ci) => ({
      ...ci,
      originalColumnIndex: ci.originalColumnIndex + 1,
    })),
  ];

  // Update body data
  const newBody: AitRowGroupData<T>[] = bodyData.map((rg) => {
    return {
      ...rg,
      rows: rg.rows.map((r) => {
        const duplicate = trDuplicate(r.cells[columnIndex]);
        // Assign new unique columnId for the duplicated cell
        duplicate.columnId = newColumnId;
        const newCells = [
          ...r.cells.slice(0, columnIndex + 1),
          duplicate,
          ...r.cells.slice(columnIndex + 1),
        ];
        return { ...r, cells: newCells };
      }),
    };
  });
  // Update header group
  const newHeader: AitHeaderGroupData<T> | false =
    headerData === false
      ? false
      : {
          ...headerData,
          rows: headerData.rows.map((r) => {
            const newHeaderCells = [...r.cells];
            // Check for colSpan
            if (r.cells[columnIndex + 1]?.colSpan === 0) {
              // Change colSpan on previous spanner
              let lookback = 1;
              while (lookback <= columnIndex && r.cells[columnIndex + 1 - lookback].colSpan === 0)
                lookback++;
              r.cells[columnIndex + 1 - lookback].colSpan =
                r.cells[columnIndex + 1 - lookback].colSpan + 1;
              // Add in blank cell
              const n = newHeaderCell<T>(defaultCellWidth, blankT, newColumnId);
              n.colSpan = 0;
              newHeaderCells.splice(columnIndex + 1, 0, n);
            } else {
              const duplicate = trDuplicate(r.cells[columnIndex]);
              duplicate.colSpan = 1;
              // Assign new unique columnId for the duplicated cell
              duplicate.columnId = newColumnId;
              newHeaderCells.splice(columnIndex + 1, 0, duplicate);
            }
            return { ...r, cells: newHeaderCells };
          }),
        };
  return { headerData: newHeader, bodyData: newBody, columnInformation: updatedColumnInformation };
};
